import NearMeIcon from "@mui/icons-material/NearMe";
import Crop169Icon from "@mui/icons-material/Crop169";
import CategoryIcon from "@mui/icons-material/Category";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import StraightenIcon from "@mui/icons-material/Straighten";
import ExploreIcon from "@mui/icons-material/Explore";
import FlightIcon from "@mui/icons-material/Flight";
import MarginIcon from "@mui/icons-material/Margin";
import {
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Ramp } from "../../../../../types";
import { useMeasuringToolState } from "../../../../../containers/MeasuringToolContainer";
import { useSnackbar } from "../../../../../containers/SnackbarContainer";
import { useLocalRampState } from "../../RampEditor/RampEditorPresenter";
import {
  getDefaultSizeCategory,
  Shape,
  SIZE_CATEGORY_OPTIONS,
} from "../../RampEditor/RampCanvas/RampCanvas";
import { useReferenceAircraftDialogState } from "../RampEditorV2Presenter";

type Tool =
  | "Select"
  | "Area"
  | "Zone"
  | "Line"
  | "Tie Down T"
  | "Double T"
  | "Helipad"
  | "Spot"
  | "Text"
  | "Measuring"
  | "Compass Rose"
  | "Reference Aircraft";

type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  ramp: Ramp;
};

// Primary drawing tools (Spot is handled separately with SplitButton)
const PRIMARY_TOOLS: { id: Tool; icon: React.ReactNode; label: string }[] = [
  { id: "Select", icon: <NearMeIcon fontSize="small" />, label: "Select (V)" },
  { id: "Area", icon: <Crop169Icon fontSize="small" />, label: "Area (A)" },
  { id: "Zone", icon: <CategoryIcon fontSize="small" />, label: "Zone (Z)" },
  { id: "Line", icon: <HorizontalRuleIcon fontSize="small" />, label: "Line (L)" },
  { id: "Spot", icon: <MarginIcon fontSize="small" />, label: "Spot (S)" },
  { id: "Text", icon: <TextFieldsIcon fontSize="small" />, label: "Text (T)" },
];

// Secondary/specialty tools
const SECONDARY_TOOLS: { id: Tool; icon: React.ReactNode; label: string }[] = [
  { id: "Tie Down T", icon: <span style={{ fontWeight: "bold", fontSize: 14 }}>T</span>, label: "Tie Down T" },
  { id: "Double T", icon: <span style={{ fontWeight: "bold", fontSize: 14 }}>TT</span>, label: "Double T" },
  { id: "Helipad", icon: <span style={{ fontWeight: "bold", fontSize: 14 }}>H</span>, label: "Helipad" },
  { id: "Compass Rose", icon: <ExploreIcon fontSize="small" />, label: "Compass Rose" },
];

// Utility tools
const UTILITY_TOOLS: { id: Tool; icon: React.ReactNode; label: string }[] = [
  { id: "Measuring", icon: <StraightenIcon fontSize="small" />, label: "Measuring" },
  { id: "Reference Aircraft", icon: <FlightIcon fontSize="small" />, label: "Reference Aircraft" },
];


export const CompactToolbar: React.FC<Props> = ({
  selectedTool,
  setSelectedTool,
  ramp,
}) => {
  const { t } = useTranslation();
  const { setRamp, setActiveShapes } = useLocalRampState();
  const measuringToolState = useMeasuringToolState();
  const snackbar = useSnackbar();
  const { setOpen: setReferenceAircraftDialogOpen } = useReferenceAircraftDialogState();

  // State for spot size menu
  const [spotMenuAnchor, setSpotMenuAnchor] = React.useState<HTMLElement | null>(null);
  const spotMenuOpen = Boolean(spotMenuAnchor);

  // Starting point for new shapes (center of ramp in FEET, not pixels)
  const startingPoint = { x: (ramp?.width ?? 200) / 2, y: (ramp?.depth ?? 200) / 2 };

  const DEFAULT_SPOT_COLOR = "#FFFFFF";

  const createShape = (type: Shape["type"], extraProps: Partial<Shape> = {}) => {
    if (!ramp?.fbo_id || !ramp?.id) return;
    setActiveShapes([]);
    const newShape: Shape = {
      id: uuidv4(),
      fbo_id: ramp.fbo_id,
      ramp_id: ramp.id,
      tags: [],
      type,
      locked: false,
      points: [
        { x: startingPoint.x - 50, y: startingPoint.y },
        { x: startingPoint.x + 50, y: startingPoint.y },
        { x: startingPoint.x + 50, y: startingPoint.y + 100 },
        { x: startingPoint.x - 50, y: startingPoint.y + 100 },
      ],
      stroke_style: "#000000",
      ...extraProps,
    };
    setRamp((currentRamp) => ({
      ...currentRamp,
      shapes: [newShape, ...currentRamp.shapes],
    }));
    setSelectedTool("Select");
    measuringToolState.setActive(false);
  };

  // Create a SQUARE spot - matches existing RampEditor Toolbar.tsx exactly
  const createSpotShape = (size: number) => {
    if (!ramp?.fbo_id || !ramp?.id) return;
    setActiveShapes([]);

    // Get label and size category for this spot
    const sizeOption = SIZE_CATEGORY_OPTIONS.find((opt) => opt.maxFeet === size);
    const sizeCategory = getDefaultSizeCategory(size);

    setRamp((currentRamp) => ({
      ...currentRamp,
      shapes: [
        {
          id: uuidv4(),
          fbo_id: ramp.fbo_id,
          ramp_id: ramp.id,
          tags: ["spot"],
          type: "Spot",
          locked: false,
          points: [
            { x: startingPoint.x - size / 2, y: startingPoint.y - size / 2 },
            { x: startingPoint.x + size / 2, y: startingPoint.y - size / 2 },
            { x: startingPoint.x + size / 2, y: startingPoint.y + size / 2 },
            { x: startingPoint.x - size / 2, y: startingPoint.y + size / 2 },
          ],
          stroke_style: DEFAULT_SPOT_COLOR,
          line_style: "dashed",
          line_width: 3,
          fill_style: null,
          font_size: 14,
          reference_image_height: null,
          text: "",
          reference_points: [
            {
              name: "north",
              validOrientations: ["nose_to_line", "tail_to_line"],
            },
          ],
          metadata: {
            allowedSizes: [sizeCategory],
            egressLanes: [],
          },
        },
        ...currentRamp.shapes,
      ],
    }));
    setSelectedTool("Select");
    setSpotMenuAnchor(null);
    measuringToolState.setActive(false);
  };

  const handleToolSelect = (toolId: string) => {
    switch (toolId) {
      case "Tie Down T":
        createShape("Tie Down T");
        break;
      case "Double T":
        createShape("Double T");
        break;
      case "Helipad":
        createShape("Helipad", {
          stroke_style: "#f9de55",
          line_style: "solid",
          line_width: 3,
          font_size: 50,
          text: "H",
        });
        break;
      case "Compass Rose": {
        const currentRamp = useLocalRampState.getState().ramp;
        if (currentRamp?.shapes?.find((s) => s.type === "Compass Rose")) {
          snackbar.notify({
            text: t("settings.rampsSettings.rampEditor.compassRoseExists", "A Compass Rose already exists on this ramp"),
            severity: "warning",
          });
          return;
        }
        createShape("Compass Rose");
        break;
      }
      case "Spot":
        // Spot tool opens a menu to select size - handled by renderToolButton
        // This case is here for completeness but the menu is opened via onClick
        break;
      case "Measuring":
        setActiveShapes([]);
        measuringToolState.setActive(true);
        setSelectedTool(toolId);
        break;
      case "Reference Aircraft":
        setActiveShapes([]);
        setReferenceAircraftDialogOpen(true);
        measuringToolState.setActive(false);
        break;
      default:
        setSelectedTool(toolId);
        measuringToolState.setActive(false);
    }
  };

  const renderToolButton = (tool: { id: Tool; icon: React.ReactNode; label: string }) => {
    // Special handling for Spot tool - IconButton that opens size menu
    if (tool.id === "Spot") {
      return (
        <Tooltip key={tool.id} title={tool.label} placement="right">
          <IconButton
            size="small"
            onClick={(event) => setSpotMenuAnchor(event.currentTarget)}
            sx={{
              backgroundColor: spotMenuOpen ? "primary.main" : "transparent",
              color: spotMenuOpen ? "white" : "text.primary",
              "&:hover": {
                backgroundColor: spotMenuOpen ? "primary.dark" : "action.hover",
              },
              borderRadius: 1,
              width: 36,
              height: 36,
            }}
          >
            {tool.icon}
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip key={tool.id} title={tool.label} placement="right">
        <IconButton
          size="small"
          onClick={() => handleToolSelect(tool.id)}
          sx={{
            backgroundColor: selectedTool === tool.id ? "primary.main" : "transparent",
            color: selectedTool === tool.id ? "white" : "text.primary",
            "&:hover": {
              backgroundColor: selectedTool === tool.id ? "primary.dark" : "action.hover",
            },
            borderRadius: 1,
            width: 36,
            height: 36,
          }}
        >
          {tool.icon}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 0.5,
          gap: 0.25,
          borderRadius: 2,
        }}
      >
        {PRIMARY_TOOLS.map(renderToolButton)}

        <Divider sx={{ my: 0.5 }} />

        {SECONDARY_TOOLS.map(renderToolButton)}

        <Divider sx={{ my: 0.5 }} />

        {UTILITY_TOOLS.map(renderToolButton)}
      </Paper>

      {/* Spot size selection menu */}
      <Menu
        anchorEl={spotMenuAnchor}
        open={spotMenuOpen}
        onClose={() => setSpotMenuAnchor(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          {t("settings.rampsSettings.rampEditor.toolbar.selectSpotSize", "Select spot size")}
        </Typography>
        <MenuItem onClick={() => createSpotShape(40)}>
          {t("settings.rampsSettings.rampEditor.toolbar.spotSizes.verySmall")}
        </MenuItem>
        <MenuItem onClick={() => createSpotShape(60)}>
          {t("settings.rampsSettings.rampEditor.toolbar.spotSizes.small")}
        </MenuItem>
        <MenuItem onClick={() => createSpotShape(80)}>
          {t("settings.rampsSettings.rampEditor.toolbar.spotSizes.midsize")}
        </MenuItem>
        <MenuItem onClick={() => createSpotShape(100)}>
          {t("settings.rampsSettings.rampEditor.toolbar.spotSizes.superMid")}
        </MenuItem>
        <MenuItem onClick={() => createSpotShape(120)}>
          {t("settings.rampsSettings.rampEditor.toolbar.spotSizes.large")}
        </MenuItem>
      </Menu>
    </>
  );
};
